import { createContext, ReactNode, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { Customer } from '@adapters/bigcommerce/types/customer';
import klaviyoRegisterUser from '@adapters/klaviyo/operations/analytics/registerUser';

type CustomerProviderProps = { children: ReactNode };
type CustomerContextType = {
  customer: Customer | null;
  customerIsLoggedIn: boolean;
  customerAuthReady: boolean;
  userLoggedIn: () => void;
  userLoggedOut: () => void;
};

const CUSTOMER_CACHE_KEY = 'customer_cache';

// Server-safe default so SSR and first client render match (avoids hydration mismatch).
// Real auth state is applied in useEffect after mount.
const initialState: CustomerContextType = {
  customer: null,
  customerIsLoggedIn: false,
  customerAuthReady: false,
  userLoggedIn: () => {},
  userLoggedOut: () => {},
};

export const CustomerContext = createContext<CustomerContextType>(initialState);
export function CustomerProvider({ children }: CustomerProviderProps) {
  const [customerIsLoggedIn, setCustomerIsLoggedIn] = useState(
    initialState.customerIsLoggedIn
  );
  const [customerAuthReady, setCustomerAuthReady] = useState(
    initialState.customerAuthReady
  );
  const [customer, setCustomer] = useState(initialState.customer);
  const hasCustomerData = customer !== null && customer?.firstName !== 'GUEST';

  function resetCustomerCache() {
    localStorage.removeItem(CUSTOMER_CACHE_KEY);
  }

  function userLoggedIn() {
    setCustomerIsLoggedIn(true);
    resetCustomerCache();
  }

  function userLoggedOut() {
    setCustomerIsLoggedIn(false);
    resetCustomerCache();
  }

  // After mount: restore auth state from cookie/localStorage (client-only; avoids hydration mismatch).
  useEffect(() => {
    const hasCookie = !!Cookies.get('mecBcCustomerToken');
    if (hasCookie) {
      setCustomerIsLoggedIn(true);
      try {
        const cached = localStorage.getItem(CUSTOMER_CACHE_KEY);
        if (cached) setCustomer(JSON.parse(cached));
      } catch {
        // ignore invalid cache
      }
    }
    setCustomerAuthReady(true);
  }, []);

  useEffect(() => {
    if (customerIsLoggedIn && customer) {
      klaviyoRegisterUser({
        email: customer.email,
        firstName: customer?.firstName,
        lastName: customer?.lastName,
      });
    }

    (async () => {
      if (customerIsLoggedIn && !hasCustomerData) {
        try {
          const response = await fetch('/api/customer');
          let { data } = await response.json();
          if (data?.firstName === 'GUEST') {
            data = null;
          }

          localStorage.setItem(CUSTOMER_CACHE_KEY, JSON.stringify(data));
          setCustomer(data);
        } catch (err) {
          console.error(err);
        }
      }
    })();
  }, [customer, customerIsLoggedIn, hasCustomerData]);

  return (
    <CustomerContext.Provider
      value={{
        customer,
        customerIsLoggedIn,
        customerAuthReady,
        userLoggedIn,
        userLoggedOut,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
}
